<template>
  <!--
    The flag cannot be read when Angular configures its routes: in production it comes
    from /me, and locally from env.json via window.leanlaw.features, neither of which
    is populated that early. So the route always mounts this, and the choice is made
    once /me has resolved — rendering the old page first and swapping would be worse
    than a moment of skeleton.
  -->
  <v-skeleton-loader
    v-if="uiState.resolving"
    class="pa-6"
    type="heading, paragraph, card"
  />

  <ConflictCheckPage
    v-else-if="isEnabled"
    :state="state"
    :state-params="stateParams"
    :root-scope="rootScope"
    :tracking="tracking"
    data-cy="conflict-check-v2"
  />

  <ConflictCheck
    v-else
    :lean-law-api="leanLawApi"
    :alerts="alerts"
    :state="state"
    :state-params="stateParams"
    :root-scope="rootScope"
    data-cy="conflict-check-v1"
  />
</template>

<script setup lang="ts">
import { onMounted, reactive } from 'vue';
import { useMeStore } from '@/services/UserService/useMeStore';
import ConflictCheck from '../ConflictCheck.vue';
import ConflictCheckPage from './ConflictCheckPage.vue';
import { useConflictCheckFlag } from './composables/useConflictCheckFlag';

// leanLawApi and alerts are injected onto every component by the bridge's global
// mixin; they are declared here so they can be forwarded to the legacy page, which
// still takes them as props.
defineProps<{
  leanLawApi?: Record<string, unknown>;
  alerts?: Record<string, unknown>;
  state?: Record<string, unknown>;
  stateParams?: Record<string, unknown>;
  rootScope?: Record<string, unknown>;
  tracking?: Record<string, unknown>;
}>();

const meStore = useMeStore();
const { isEnabled } = useConflictCheckFlag();

const uiState = reactive({ resolving: true });

onMounted(async () => {
  try {
    await meStore.getMe();
  } catch {
    // Swallowed deliberately. getMe already reports its own failure, and the only
    // decision here is which page to render — a failed /me leaves the flag false,
    // which lands on the page that works today. Rethrowing would surface as an
    // unhandled rejection and still show that page.
  } finally {
    uiState.resolving = false;
  }
});
</script>
